// Editar a divisão por área e o CNAE de um contrato (04/10/2026).
// Abre do cartão "Áreas e CNAE" (Visão geral) e da vista de pendências — uma
// janela só, dois lugares. Quem grava e decide o que vai às parcelas é o
// servidor (`op_definir_areas_contrato`); aqui a pessoa escolhe e confere.
//
// Regras que a janela explica na própria tela:
//  • a divisão é o padrão das parcelas NOVAS; parcela sem setor recebe agora;
//    parcela que já tem setor só muda se marcar a opção — e só as em aberto;
//  • o CNAE vai às parcelas em aberto e sem nota que estavam vazias ou com o
//    CNAE antigo do contrato; exceção feita à mão numa parcela fica.
//
// 🚨 Só vai ao servidor o que a pessoa MUDOU em relação ao que vale hoje
// (próprio ou herdado do principal). A janela abre preenchida com o que vale;
// mandar tudo de volta fixaria no aditivo uma cópia da divisão do principal, e
// ele deixaria de acompanhá-lo sem ninguém ter pedido (revisão de 05/10).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Select, Radio, Checkbox, Button, Alert, Spin, Tag, Typography, App as AntApp } from 'antd';
import { AREAS_EMPRESA, casaBusca } from '../../utils/areasEmpresa';
import { OPCOES_CNAE, cnaeValido } from '../../utils/cnaes';
import {
  DetalheAreas, CONFIANCA, rotuloOrigem, descreverRateio,
  rateioParaPct, pctParaRateio, somaPct, somaFecha, temNegativo, tudoEm, mesmoRateio,
} from './areasContrato';
import { aceitarSetorCusto } from '../administrativo-plano-acao/paginas/apiDre';
import { carregarAreasDoContrato, definirAreasDoContrato, mensagemDeErro, resumoAplicado } from './apiAreas';

export function ModalAreasContrato({ contratoId, aberto, onFechar, onSalvo, podeAceitar = false, aceiteContratoExiste = false }: {
  podeAceitar?: boolean;
  aceiteContratoExiste?: boolean;
  contratoId: string | null;
  aberto: boolean;
  onFechar: () => void;
  onSalvo: (d: DetalheAreas) => void;
}) {
  const { message } = AntApp.useApp();
  const [det, setDet] = useState<DetalheAreas | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pct, setPct] = useState<Record<string, string>>({});
  const [cnae, setCnae] = useState<string | null>(null);
  const [modo, setModo] = useState<'sem_setor' | 'abertas'>('sem_setor');
  const [levarCnae, setLevarCnae] = useState(true);
  const [seguirContrato, setSeguirContrato] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // O que a janela preencheu sozinha com a sugestão (avisa em vez de esconder).
  const [semeou, setSemeou] = useState<{ divisao: boolean; cnae: boolean }>({ divisao: false, cnae: false });
  // Resposta atrasada do contrato A não pode cair na janela já aberta para o B
  // (nem reescrever o que a pessoa começou a digitar): só vale a do pedido atual.
  const pedidoAtual = useRef(0);

  async function carregar(id: string) {
    const meu = ++pedidoAtual.current;
    setCarregando(true); setErro(null); setDet(null);
    try {
      const d = await carregarAreasDoContrato(id);
      if (meu !== pedidoAtual.current) return;
      setDet(d);
      // Contrato sem divisão/CNAE: a janela já abre com a sugestão do servidor
      // (e diz que foi ela que preencheu). Com o que já vale, abre com isso.
      const sugD = !d.divisao.rateio ? d.sugestao.divisao : null;
      const sugC = !d.cnae.codigo ? d.sugestao.cnae : null;
      setPct(rateioParaPct(d.divisao.rateio || (sugD && sugD.rateio) || null));
      setCnae(d.cnae.codigo || (sugC && sugC.codigo) || null);
      setSemeou({ divisao: !!sugD, cnae: !!sugC });
      setModo('sem_setor'); setLevarCnae(true); setSeguirContrato(false);
    } catch (e: any) {
      if (meu !== pedidoAtual.current) return;
      setErro(mensagemDeErro(e));
    } finally {
      if (meu === pedidoAtual.current) setCarregando(false);
    }
  }
  useEffect(() => {
    if (aberto && contratoId) carregar(contratoId);
    else pedidoAtual.current++;          // fechou: resposta pendente não vale mais
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, contratoId]);

  const soma = somaPct(pct);
  const vazio = soma === 0;
  const rateioNovo = useMemo(() => pctParaRateio(pct), [pct]);
  const somaOk = vazio || (somaFecha(pct) && !temNegativo(pct));
  // "Mudou" é contra o que VALE hoje — próprio ou do contrato principal.
  const valeDivisao = det?.divisao.rateio || null;
  const valeCnae = det?.cnae.codigo || null;
  const mudouDivisao = !!rateioNovo && !mesmoRateio(rateioNovo, valeDivisao);
  const mudouCnae = !!cnae && cnae !== valeCnae;
  const divisaoDepois = mudouDivisao ? rateioNovo : valeDivisao;
  const cnaeDepois = mudouCnae ? cnae : valeCnae;
  const levaSetor = !!det && !!divisaoDepois
    && (det.numeros.sem_setor_n > 0 || (modo === 'abertas' && det.numeros.abertas_n > 0));
  const levaCnae = !!det && levarCnae && !!cnaeDepois
    && (det.numeros.abertas_sem_cnae_n > 0 || (mudouCnae && det.numeros.abertas_n > 0));
  const aceitaGastos = podeAceitar && !aceiteContratoExiste && seguirContrato;
  // Por que o Salvar está apagado — dito na tela, não só no botão cinza.
  const motivoBloqueio = !det ? 'Carregando…'
    : !somaOk ? (temNegativo(pct) ? 'Percentual negativo não vale.' : `A divisão soma ${String(soma).replace('.', ',')}% — precisa fechar 100%.`)
    : (cnae && !cnaeValido(cnae)) ? 'Escolha um CNAE da lista.'
    : (!mudouDivisao && !mudouCnae && !levaSetor && !levaCnae && !aceitaGastos) ? 'Nada mudou.'
    : null;

  async function salvar() {
    if (!det || motivoBloqueio || salvando || det.contrato.id !== contratoId) return;
    setSalvando(true);
    try {
      const r = await definirAreasDoContrato({
        contrato_id: det.contrato.id,
        ...(mudouDivisao && rateioNovo ? { rateio: rateioNovo } : {}),
        ...(mudouCnae && cnae ? { cnae } : {}),
        aplicar_parcelas: modo,
        aplicar_cnae_parcelas: levarCnae,
      });
      const extra = resumoAplicado(r.aplicado);
      let gastos = false;
      if (aceitaGastos) {
        // Falha aqui NÃO desfaz o que já foi salvo: só avisa.
        try { await aceitarSetorCusto({ contrato_id: det.contrato.id, escopo: 'contrato', ref: null }); gastos = true; }
        catch (e: any) { message.warning('As áreas foram salvas, mas o aceite dos gastos não: ' + (e?.message || mensagemDeErro(e))); }
      }
      const partes = [extra, gastos ? 'custos que não casam passam a seguir o contrato — recalcule a Margem' : ''].filter(Boolean).join(' — ');
      message.success(r.sem_mudanca && !gastos ? 'Nada mudou — já estava assim.' : `Áreas e CNAE salvos${partes ? ' — ' + partes : ''}.`);
      onSalvo(r);
    } catch (e: any) {
      message.error('Não salvou: ' + mensagemDeErro(e));
    } finally { setSalvando(false); }
  }

  const sugD = det?.sugestao.divisao || null;
  const sugC = det?.sugestao.cnae || null;
  const principal = det?.contrato.pai_numero || 'principal';

  return (
    <Modal
      open={aberto}
      onCancel={() => { if (!salvando) onFechar(); }}
      title={det ? `Áreas e CNAE — ${det.contrato.numero}` : 'Áreas e CNAE do contrato'}
      width="94vw" style={{ maxWidth: 940, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      destroyOnHidden
      footer={[
        // O aviso da soma já está ao lado dos campos: aqui só os outros motivos.
        <Typography.Text key="motivo" type="secondary" style={{ fontSize: 12, marginRight: 12 }}>
          {det && motivoBloqueio && somaOk ? motivoBloqueio : ''}
        </Typography.Text>,
        <Button key="cancelar" onClick={onFechar} disabled={salvando}>Cancelar</Button>,
        <Button key="salvar" type="primary" onClick={salvar} loading={salvando} disabled={!!motivoBloqueio}>Salvar</Button>,
      ]}>
      {carregando && <div style={{ textAlign: 'center', padding: 50 }}><Spin /></div>}
      {erro && (
        <Alert type="error" showIcon message="Não consegui carregar as áreas deste contrato" description={erro}
          action={contratoId ? <Button size="small" onClick={() => carregar(contratoId)}>Tentar de novo</Button> : undefined} />
      )}
      {det && !carregando && (
        <div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
            {det.contrato.titulo}
            {det.contrato.pai_numero ? ` · aditivo de ${det.contrato.pai_numero}` : ''}
            {det.contrato.aditivos ? ` · ${det.contrato.aditivos} aditivo(s): os que não têm divisão/CNAE próprios acompanham este contrato` : ''}
          </Typography.Paragraph>

          {(semeou.divisao || semeou.cnae) && (
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="Preenchi com a sugestão do sistema — confira antes de salvar"
              description={
                <span style={{ fontSize: 12 }}>
                  {semeou.divisao && sugD && <>Divisão — origem: {rotuloOrigem(sugD.origem)}{sugD.motivo ? ` (${sugD.motivo})` : ''}
                    {sugD.alternativa ? `; alternativa: ${descreverRateio(sugD.alternativa)}` : ''}. </>}
                  {semeou.cnae && sugC && <>CNAE — origem: {rotuloOrigem(sugC.origem)}{sugC.motivo ? ` (${sugC.motivo})` : ''}.</>}
                </span>
              } />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 20 }}>
            {/* ── Divisão por área ── */}
            <div>
              <Typography.Text strong>Divisão por área</Typography.Text>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                É com ela que as parcelas novas nascem. Clique no nome da área para pôr 100% nela.
              </div>
              {AREAS_EMPRESA.map(a => (
                <div key={a.chave} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: a.cor, flexShrink: 0 }} />
                  <button type="button" onClick={() => { setPct(tudoEm(a.chave)); setSemeou(s => ({ ...s, divisao: false })); }}
                    title={`Pôr 100% em ${a.rotulo}`}
                    style={{ flex: 1, textAlign: 'left', border: 'none', background: 'transparent', padding: 0,
                      cursor: 'pointer', fontSize: 13, color: '#222' }}>
                    {a.rotulo}
                  </button>
                  <input value={pct[a.chave] ?? ''} inputMode="decimal" placeholder="0"
                    aria-label={`Percentual de ${a.rotulo}`}
                    onChange={e => { setPct({ ...pct, [a.chave]: e.target.value }); setSemeou(s => ({ ...s, divisao: false })); }}
                    style={{ width: 70, padding: '4px 6px', fontSize: 13, textAlign: 'right',
                      border: '1px solid #d9d9d9', borderRadius: 6 }} />
                  <span style={{ color: '#888', fontSize: 12, width: 12 }}>%</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingTop: 6, borderTop: '1px solid #f0f0f0' }}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: vazio ? '#888' : somaOk ? '#0a7f4a' : '#B91626' }}>
                  {vazio
                    ? (valeDivisao ? `Em branco: continua valendo ${descreverRateio(valeDivisao)}` : 'Sem divisão informada')
                    : `Soma: ${String(soma).replace('.', ',')}% ${somaOk ? '✓' : '— precisa fechar 100%'}`}
                </span>
              </div>
              {sugD && !semeou.divisao && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  Sugestão <Tag color={CONFIANCA[sugD.confianca].cor} style={{ marginInline: 2 }}>{CONFIANCA[sugD.confianca].rotulo}</Tag>
                  {descreverRateio(sugD.rateio)} — {rotuloOrigem(sugD.origem)}
                  <Button size="small" type="link" style={{ padding: '0 4px', height: 18 }}
                    onClick={() => { setPct(rateioParaPct(sugD.rateio)); setSemeou(s => ({ ...s, divisao: true })); }}>usar</Button>
                </div>
              )}
              {det.divisao.herdada && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  Este aditivo segue o contrato {principal}. Só passa a ter divisão própria se você escolher uma <b>diferente</b> aqui.
                </div>
              )}
            </div>

            {/* ── CNAE ── */}
            <div>
              <Typography.Text strong>CNAE do contrato (código de serviço da nota)</Typography.Text>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                É o código que soma mais valor entre os serviços do contrato. Parcela nova nasce com ele;
                na parcela dá para trocar quando uma nota for de serviço diferente.
              </div>
              <Select
                showSearch placeholder="Escolha o código (digite para buscar)"
                // Com CNAE já definido não há "limpar": o contrato não volta a ficar sem código.
                allowClear={!valeCnae}
                style={{ width: '100%' }} value={cnae || undefined}
                onChange={(v) => { setCnae(v || null); setSemeou(s => ({ ...s, cnae: false })); }}
                options={OPCOES_CNAE}
                filterOption={(digitado, op) => casaBusca(digitado, op?.label)}
                status={cnae && !cnaeValido(cnae) ? 'error' : undefined} />
              {sugC && !semeou.cnae && cnae !== sugC.codigo && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  Sugestão <Tag color={CONFIANCA[sugC.confianca].cor} style={{ marginInline: 2 }}>{CONFIANCA[sugC.confianca].rotulo}</Tag>
                  {sugC.codigo} — {rotuloOrigem(sugC.origem)}{sugC.motivo ? ` (${sugC.motivo})` : ''}
                  <Button size="small" type="link" style={{ padding: '0 4px', height: 18 }}
                    onClick={() => { setCnae(sugC.codigo); setSemeou(s => ({ ...s, cnae: true })); }}>usar</Button>
                </div>
              )}
              {det.cnae.herdado && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  Este aditivo usa o CNAE do contrato {principal}. Só passa a ter código próprio se você escolher um <b>diferente</b> aqui.
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <Typography.Text strong>O que fazer com as parcelas</Typography.Text>
                <Radio.Group value={modo} onChange={e => setModo(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  <Radio value="sem_setor">
                    Preencher só as parcelas <b>sem setor</b>
                    <span style={{ color: '#888' }}> ({det.numeros.sem_setor_n} hoje)</span>
                  </Radio>
                  <Radio value="abertas">
                    Também <b>trocar o setor</b> das parcelas em aberto, sem nota
                    <span style={{ color: '#888' }}> ({det.numeros.abertas_n} em aberto) — as já faturadas não mudam</span>
                  </Radio>
                </Radio.Group>
                <Checkbox checked={levarCnae} onChange={e => setLevarCnae(e.target.checked)} style={{ marginTop: 10 }}>
                  Levar o CNAE às parcelas em aberto e sem nota
                  <span style={{ color: '#888' }}> ({det.numeros.abertas_sem_cnae_n} sem código) — exceção feita à mão numa parcela fica</span>
                </Checkbox>
              </div>

              {(podeAceitar || aceiteContratoExiste) && (
                <div style={{ marginTop: 18 }}>
                  <Typography.Text strong>O que fazer com os gastos</Typography.Text>
                  {aceiteContratoExiste
                    ? <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>Já registrado: os custos que não casam seguem o contrato.</div>
                    : <div style={{ marginTop: 8 }}>
                        <Checkbox checked={seguirContrato} onChange={e => setSeguirContrato(e.target.checked)}>
                          Custos que não casam com esta divisão seguem o contrato
                          <span style={{ color: '#888' }}> (aceite registrado com o seu nome)</span>
                        </Checkbox>
                      </div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
